import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({ name: '', terms_days: 0, terms_type: 'from_invoice_date' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({ name: customer.name, terms_days: customer.terms_days, terms_type: customer.terms_type || 'from_invoice_date' });
    } else {
      setEditingCustomer(null);
      setFormData({ name: '', terms_days: 0, terms_type: 'from_invoice_date' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success('Customer created');
      }
      setDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Customers
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your customers and their payment terms
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-primary hover:bg-primary/90"
                onClick={() => handleOpenDialog()}
                data-testid="add-customer-btn"
              >
                <Plus className="w-4 h-4" />
                Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingCustomer ? 'Edit Customer' : 'Add Customer'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Customer Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter customer name"
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="terms_days">Payment Terms (days)</Label>
                  <Input
                    id="terms_days"
                    type="number"
                    min="0"
                    value={formData.terms_days}
                    onChange={(e) => setFormData({ ...formData, terms_days: parseInt(e.target.value) || 0 })}
                    placeholder="e.g., 30"
                    data-testid="customer-terms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Terms Calculated From</Label>
                  <Select
                    value={formData.terms_type}
                    onValueChange={(value) => setFormData({ ...formData, terms_type: value })}
                  >
                    <SelectTrigger data-testid="customer-terms-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="from_invoice_date">From Invoice Date</SelectItem>
                      <SelectItem value="from_end_of_month">From End of Month</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.terms_type === 'from_end_of_month' 
                      ? 'Days are calculated from the last day of the invoice month'
                      : 'Days are calculated from the invoice date'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-customer-btn">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-customer-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingCustomer ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {customers.length === 0 ? (
              <div className="empty-state py-16">
                <Users className="empty-state-icon w-16 h-16 text-slate-300 mb-4" />
                <h3 className="empty-state-title text-lg font-semibold text-slate-900 mb-2">No customers yet</h3>
                <p className="empty-state-text text-sm text-slate-500 max-w-md">
                  Add your first customer to start tracking invoice payments and cash flow timing.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b">
                  <Input
                    type="search"
                    placeholder="Search customers by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="max-w-sm"
                    data-testid="customers-search-input"
                  />
                </div>
                <div className="overflow-x-auto">
                <Table data-testid="customers-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Customer Name</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Payment Terms</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers
                      .filter((c) => !searchQuery || (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((customer) => (
                      <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                        <TableCell className="font-medium text-slate-900">{customer.name}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {customer.terms_days} days
                          <span className="text-xs text-slate-400 ml-1">
                            {customer.terms_type === 'from_end_of_month' ? '(EOM)' : ''}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(customer)}
                              data-testid={`edit-customer-${customer.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-customer-${customer.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Customer</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{customer.name}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(customer.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid={`confirm-delete-customer-${customer.id}`}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
